/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
 * content, please contact the author for proper authorization and agreement.
 */

import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'

export const useCompanyContactSchema = () => {
    const { t } = useTranslation()

    return useMemo(() => {
        const objectSchema = z.object({
            first_name: z
                .string()
                .trim()
                .max(80, t('First name must be at most 80 characters'))
                .optional()
                .nullable()
                .or(z.literal('')),
            last_name: z
                .string()
                .trim()
                .max(80, t('Last name must be at most 80 characters'))
                .optional()
                .nullable()
                .or(z.literal('')),
            email: z.string().trim().min(1, t('Email is required')).email(t('Invalid email format')),
            phone: z
                .string()
                .trim()
                .min(12, t('Phone must be at least 12 characters'))
                .max(20, t('Phone must be at most 20 characters')),
            position: z.string().trim().optional().nullable().or(z.literal('')),
            title: z.string().trim().optional().nullable().or(z.literal('')),
            custom_title: z.string().trim().optional().nullable().or(z.literal('')),
            note: z.string().trim().optional().nullable().or(z.literal('')),
            gender: z.enum(['male', 'female', 'other']).optional().nullable().or(z.literal('')),
            origin: z.string().optional().nullable().or(z.literal('')),
            tags: z.array(z.string()).optional().nullable(),
            seller_id: z.number().min(1, t('Seller is required')),
            is_generic: z.boolean().optional().nullable(),
        })

        const refined = objectSchema.superRefine((data, ctx) => {
            if (data.is_generic) {
                return
            }
            if (!data.first_name?.trim()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['first_name'],
                    message: t('First name is required'),
                })
            }
            if (!data.last_name?.trim()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['last_name'],
                    message: t('Last name is required'),
                })
            }
        })

        // Keep shape for useRequiredField (ZodEffects hides object.shape).
        return Object.assign(refined, { shape: objectSchema.shape })
    }, [t])
}

export type CompanyContactInputs = z.infer<ReturnType<typeof useCompanyContactSchema>>
